/**
 * Night-mode dimming — one home for turning the night palette + the user's
 * brightness setting into what the page actually shows. Two jobs:
 *
 *   1. Flag the active palette on the document root (data-acp-palette), so the
 *      shell CSS can red-shift its frosted-white surfaces at night. Without this
 *      the UI stayed bright-white regardless of palette, which wrecks the night
 *      vision the night chart palette is trying to protect.
 *   2. Drive a whole-screen dim veil via a CSS variable, so a brightness slider
 *      can knock back anything still too bright (the satellite overlay, an input
 *      panel, any surface we didn't individually recolour). Belt and braces:
 *      recolour the big surfaces, and dim the rest.
 *
 * Mirrors the glass.ts approach (a CSS custom property pushed onto the root).
 */

/** The CSS custom property the whole-screen night veil reads for its alpha. */
export const NIGHT_DIM_VAR = '--acp-night-dim';
/** The attribute the shell CSS keys its night overrides off. */
export const PALETTE_ATTR = 'data-acp-palette';

/**
 * Veil alpha from a 0..1 brightness setting: 1 = full brightness (no veil),
 * lower = dimmer. Capped below 1 so the veil never goes fully opaque and hides
 * the chart. Only applied when the night palette is active.
 */
export function nightDimAlpha(brightness: number): number {
  const b = Number.isFinite(brightness) ? Math.min(1, Math.max(0, brightness)) : 0.75;
  return Number(((1 - b) * 0.85).toFixed(3));
}

/**
 * Push the palette flag + night veil live onto the page. No-op outside the
 * browser (SSR / tests). The veil is 0 (invisible) on any non-night palette.
 */
export function applyNight(palette: string, brightness: number): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.setAttribute(PALETTE_ATTR, palette);
  const alpha = palette === 'night' ? nightDimAlpha(brightness) : 0;
  root.style.setProperty(NIGHT_DIM_VAR, String(alpha));
}
